/* Created By: Siva Naga lingeswara Rao Chodavarapu.
 * Date: 30-JAN-2020
 * 
 * Copyright (C) Provided to Techsol by Ascensio System SIA 2012-2019.
 * All rights reserved
 *
 * https://www.onlyoffice.com/
 *
 * API Version: 7.2.1
 *
 * Below configurations might change based on version of OnlyOffice installed.
 * Please update below models according to the version pointing.
 * Currently pointing to Version: 7.2.1
 * API URL: https://api.onlyoffice.com/editors/config/
 */

/**
 * Allows to initialize or change the platform type used, document display size (width and height) and type of the document opened.
 * The Configuration required for the document server to initialize Online Document Editing/Viewing/Review (DOCX/XLSX/PPTX/PDF[view only]).
 * */
export interface OnlyOfficeConfig {
    /**
     * The editorConfig section allows to change the parameters pertaining to the editor interface:
     * opening mode (viewer or editor), interface language, additional buttons, etc.).
     * */
    editorConfig: EditorConfig;
  
    /**
     * contains all the parameters pertaining to the document (title, url, file type, etc.)
     * */
    document: Document;
  
    /**
     * Defines the document type to be opened:
     * open a text document (.doc, .docm, .docx, .dot, .dotm, .dotx, .epub, .fodt, .htm, .html, .mht, .odt, .ott, .pdf, .rtf, .txt, .djvu, .xps) for viewing or editing - text
     * open a spreadsheet (.csv, .fods, .ods, .ots, .xls, .xlsm, .xlsx, .xlt, .xltm, .xltx) for viewing or editing - spreadsheet
     * open a presentation (.fodp, .odp, .otp, .pot, .potm, .potx, .pps, .ppsm, .ppsx, .ppt, .pptm, .pptx) for viewing or editing - presentation.
     * */
    documentType: 'word' | 'cell' | 'slide';
  
    /**
     * Defines the platform type used to access the document. Can be:
     * optimized to access the document from a desktop or laptop computer - desktop
     * optimized to access the document from a tablet or a smartphone - mobile
     * specifically formed to be easily embedded into a web page - embedded.
     * The default value is "desktop".
     * */
    type?: "desktop" | "mobile" | "embedded";
  
    /**
     * Defines the encrypted signature added to the Document Server config in the form of a token.
     * */
    token?: string;
  
    /**
     * the list of special events called when some action is applied to the document (when it is loaded, modified, etc.)
     * */
    events?: Events;
  
    /**
     * Defines the document height (100% by default) in the browser window.
     * */
    height?: string;
  
    /**
     * Defines the document width (100% by default) in the browser window.
     * */
    width?: string;
  }
  
  /**
   * The editorConfig section allows to change the parameters pertaining to the editor interface:
   * (opening mode (viewer or editor), interface language, additional buttons, etc.).
   * */
  interface EditorConfig {
    /**
     * Specifies the data received from the document editing service using the onMakeActionLink event,
     * which contains the information about the bookmark in the document that will be scrolled to.
     * */
    actionLink?: any;
  
    /**
     * Specifies absolute URL to the document storage service
     * (which must be implemented by the software integrators who use ONLYOFFICE Document Server on their own server).
     * */
    callbackUrl: string;
  
    /**
     * Defines the absolute URL of the document where it will be created and available after creation.
     * If not specified, there will be no creation button.
     * */
    createUrl?: string;
  
    /**
     * The customization section allows to customize the editor interface so that it looked like your other products (if there are any)
     * and change the presence or absence of the additional buttons, links, change logos and editor owner details.
     * */
    customization?: Customization;
  
    /**
     * The embedded section is for the embedded document type only (see the config section to find out how to define the embedded document type).
     * It allows to change the settings which define the behavior of the buttons in the embedded mode.
     * */
    embedded?: Embedded;
  
    /**
     * Defines the editor interface language (if some other languages other than English are present).
     * Is set using the two letter (de, ru, it, etc.) or four letter (en-US, fr-FR, etc.) language codes.
     * The default value is "en-US".
     * */
    lang?: string;
  
    /**
     * Defines the editor opening mode.
     * Can be either view to open the document for viewing,
     * or edit to open the document in the editing mode allowing to apply changes to the document data.
     * The default value is "edit".
     * */
    mode?: "view" | "edit";
  
    /**
     * Defines the presence or absence of the documents in the Open Recent... menu option.
     * */
    recent?: Recent[];
  
    /**
     * Defines the user currently viewing or editing the document:
     * */
    user?: User;
  
    /**
     * The plugins section allows to connect the special add-ons to your
     * Document Server installation which will help you add additional features
     * to document editors.
     */
    plugins?: Plugins;
  
    fileChoiceUrl?: string;
  
    /**
     * Defines the default measurement units. Specify us or ca to set inches. The default value is "".
     */
    location?: string;
  
    canCoAuthoring?: boolean;
  
    sharingSettingsUrl?: string;
  
    saveAsUrl?: string;
    templates?: Template[];
  
    canBackToFolder:boolean;
    licenseUrl:string;
  
    customerId:string;
    region:string;
    coEditing:{},
    wopi:wopiTemplate,
  
    
  
  }
  interface wopiTemplate {
    FileNameMaxLength:number
  }
    /**
     * */
  interface Template {
    /**
     * template name
     * */
     image?: string;
    /**
     * template icon url
     * */
    url?: string;
  }
  
  /**
   * The document section allows to change all the parameters pertaining to the document (title, url, file type, etc.).
   * */
  interface Document {
    /**
     * Defines the type of the file for the source viewed or edited document.
     * Example: "docx"
     * */
    fileType?: string;
  
    /**
     * The document info section allows to change additional parameters for the document
     * (document author, folder where the document is stored, creation date, sharing settings).
     * */
    info?: Info;
  
    /**
     * Defines the unique document identifier used for document recognition by the service.
     * In case the known key is sent the document will be taken from the cache.
     * Every time the document is edited and saved, the key must be generated anew.
     * The document url can be used as the key but without the special characters and the length is limited to 20 symbols.
     * Example: "Khirz6zTPdfd7"
     * */
    key?: string;
    /**
     * The document permission section allows to change the permission for the document to be edited and downloaded or not.
     * */
    permissions?: Permissions;
  
    /**
     * Defines the desired file name for the viewed or edited document which will also be used as file name when the document is downloaded.
     * Example: "Sample Document Title.docx"
     * */
    title: string;
  
    /**
     * Defines the absolute URL where the source viewed or edited document is stored.
     * Example: "https://example.com/url-to-example-document.docx"
     * */
    url: string;
  
    vkey?: string;
  
    options: string;
  
  }
  
  /**
   * The list of special events called when some action is applied to the document (when it is loaded, modified, etc.)
   * */
  interface Events {
    /**
     * The function called when the application is loaded into the browser.
     * */
    onAppReady?: any;
  
    /**
     * The function called when the document is co-edited by the other user in the strict co-editing mode.
     * */
    onCollaborativeChanges?: any;
  
    /**
     * The function called when the document is loaded into the document editor.
     * */
    onDocumentReady?: any;
  
    /**
     * The function called when the document is modified.
     * It is called with the parameter: {"data": true} when the current user is editing the document
     * and with the parameter: {"data" : false} when the current user's changes are sent to the document editing service.
     * */
    onDocumentStateChange?: any;
  
    /**
     * the function called with the absolute URL to the edited file when the downloadAs method is being called.
     * The absolute URL to the document to be downloaded is sent in the data parameter.
     * */
    onDownloadAs?: any;
  
    /**
     * the function called when an error or some other specific event occurs.
     * The error message is sent in the data parameter.
     * */
    onError?: any;
  
    /**
     * the function called when the application opened the file.
     * The mode is sent in the data.mode parameter. Can be view or edit.
     * */
    onInfo?: any;
  
    /**
     * the function called when the user is trying to open the document which contains a bookmark, scrolling to the bookmark position.
     * To use the document bookmarks you must call the setActionLink method.
     * The bookmark data is received in the event.data parameter.
     * */
    onMakeActionLink?: any;
  
    /**
     * the function called after the error is shown, when the document is opened for editing with the old document.key value,
     * which was used to edit the previous document version and was successfully saved.
     * When this event is called the editor must be reinitialized with a new document.key.
     * */
    onOutdatedVersion?: any;
  
    /**
     * the function called when the work with the editor must be ended and the editor must be closed.
     * */
    onRequestClose?: any;
  
    /**
     * the function called when the user is trying to switch the document from the viewing into the editing mode by clicking the Edit Document button.
     * When the function is called, the editor must be initialized again, in editing mode.
     * If the method is not declared the Edit button will not be displayed.
     * */
    onRequestEditRights?: any;
  
    /**
     * the function called when the user is trying to show the document version history by clicking the Version History button.
     * To show the document version history you must call the refreshHistory method.
     * */
    onRequestHistory?: any;
  
    /**
     * the function called when the user is trying to go back to the document
     * from viewing the document version history by clicking the Back to Document button.
     * When the function is called, the editor must be initialized again, in editing mode.
     * If the method is not declared the Back to Document button will not be displayed.
     * */
    onRequestHistoryClose?: any;
  
    /**
     * the function called when the user is trying to click the specific document version in the document version history.
     * To show the changes corresponding to the specific document version you must call the setHistoryData method.
     * The document version number is sent in the data parameter.
     * */
    onRequestHistoryData?: any;
  
    /**
     * the function called when an warning occurs.
     * The warning message is sent in the data parameter.
     * */
    onWarning?: any;
  }
  
  interface anonymous{
    request: boolean, // enable set name
    label: string // postfix for user name
  }
  
  interface Review{
    hideReviewDisplay: boolean, // hide button Review mode
    hoverMode: boolean, // true - show review balloons on mouse move, not on click on text
    showReviewChanges: boolean,
    reviewDisplay: string, // original for viewer, markup for editor
    trackChanges: boolean // true/false - open editor with track changes mode on/off, owner false,only review true rest all false
  }
  interface Layout{
      toolbar:Toolbar;
      header:Header;
      leftMenu: LeftMenu;
      rightMenu:boolean; // use instead of customization.rightMenu
      statusBar:StatusBar
  }
  interface Toolbar{
    file: IFile;
    home:  Home,
    layout: boolean; // layout tab
    references:  boolean ; // de references tab
    collaboration: boolean; // collaboration tab
    protect:  boolean; // protect tab
    plugins: boolean;// plugins tab
    view: IView;
    save:boolean; 
  }
  interface IFile{
    close: boolean; // close menu button
    settings: boolean; // advanced settings
    info: boolean; // document info
    save: boolean // save button
  }
  interface Home
  {
    mailmerge: boolean;
  }
  interface IView
  {
    navigation: boolean, 
  }
  
  interface Header
  {
    users: boolean, // text language button in de/pe
    save: boolean, // document language button in de/pe
  }
  interface LeftMenu
  {
    navigation: boolean, // text language button in de/pe
    spellcheck: boolean, // document language button in de/pe
  }
  
  interface StatusBar
  {
    textLang: boolean, // text language button in de/pe
    docLang: boolean, // document language button in de/pe
    actionStatus: boolean, // status of operation
  }
  
  interface Features
  {
    spellcheck: Spellcheck, // text language button in de/pe
  }
  interface Spellcheck
  {
    mode: boolean, // text language button in de/pe
    change: boolean,
  }
  
  /**
   * The customization section allows to customize the editor interface so that it looked like your other products (if there are any)
   * and change the presence or absence of the additional buttons, links, change logos and editor owner details.
   * */
  interface Customization {
  
    compatibleFeatures:boolean;
    unit:string;
    reviewPermissions:any;
    anonymous:anonymous;
    review:Review;
    layout:Layout;
    features:Features
  
    /**
     * Defines if the Autosave menu option is enabled or disabled.
     * If set to false, only Strict co-editing mode can be selected, as Fast does not work without autosave.
     * Please note that in case you change this option in menu it will be saved to your browser localStorage.
     * The default value is true.
     * */
    autosave?: boolean;
  
    /**
     * Defines if the Chat menu button is displayed or hidden; please note that in case you hide the Chat button,
     * the corresponding chat functionality will also be disabled.
     * The default value is true.
     * */
    chat?: boolean;
  
    /**
     * Defines if the user can edit and delete only his comments.
     * The default value is false.
     * */
    commentAuthorOnly?: boolean;
  
    /**
     * Defines if the Comments menu button is displayed or hidden; please note that in case you hide the Comments button,
     * the corresponding commenting functionality will be available for viewing only,
     * the adding and editing of comments will be unavailable.
     * The default value is true.
     * */
    comments?: boolean;
  
    /**
     * Defines if the additional action buttons are displayed in the upper part of the editor window header next to the logo (false)
     * or in the toolbar (true) making the header more compact.
     * The default value is false.
     * */
    compactHeader?: boolean;
  
    /**
     * Defines if the top toolbar type displayed is full (false) or compact true.
     * The default value is false.
     * */
    compactToolbar?: boolean;
  
    /**
     * Contains the information which will be displayed int the editor About section and visible to all the editor users.
     * */
    customer?: Customer;
  
    /**
     * Defines settings for the Feedback & Support menu button.
     * Can be either boolean (simply displays or hides the Feedback & Support menu button) or object.
     * The default value is false.
     * */
    feedback?: boolean | Feedback;
  
    /**
     * Adds the request for the forced file saving to the callback handler when saving the document
     * within the document editing service (e.g. clicking the Save button, etc.).
     * The default value is false.
     * */
    forcesave?: boolean;
  
    /**
     * Defines settings for the Go to Documents menu button and upper right corner button.
     * */
    goback?: Goback;
  
    /**
     * Defines if the Help menu button is displayed or hidden.
     * The default value is true.
     * */
    help?: boolean;
  
    /**
     * Defines if the right menu is displayed or hidden on first loading.
     * The default value is false.
     * */
    hideRightMenu?: boolean;
  
    /**
     * Changes the image file at the top left corner of the Editor header.
     * The recommended image height is 20 pixels.
     * */
    logo?: Logo;
  
    /**
     * Defines if the review changes panel is automatically displayed or hidden when the editor is loaded.
     * The default value is false.
     * */
    showReviewChanges?: boolean;
  
    /**
     * Defines if the top toolbar tabs are distinctly displayed (when set to false) or only highlighted to see which one is selected (when set to true).
     * The default value is false.
     * */
    toolbarNoTabs?: boolean;
  
    /**
     * Defines the document display zoom value measured in percent.
     * Can take values larger than 0.
     * For text documents and presentations it is possible to set this parameter
     * to -1 (fitting the document to page option) or
     * to -2 (fitting the document page width to the editor page).
     * The default value is 100.
     * */
    zoom?: number;
  
    reviewDisplay?: 'original'|'final'|'markup';
  
    about?: boolean;
  
    leftMenu?: boolean;
  
    rightMenu?: boolean;
  
    toolbar?: boolean;
  
    statusBar?: boolean;
  
    toolbarHideFileName?: boolean;
  
  
    mentionShare?:boolean;
    macros?:boolean;
    plugins?:boolean;
    macrosMode:string;
    hideRulers?:boolean;
    hideNotes?:boolean;
    uiTheme?:string;
    integrationMode:string;
  }
  
  /**
   * Used for the embedded document type only and allows to change the behavior of the buttons used to control the embedded mode
   * */
  interface Embedded {
    /**
     * Defines the absolute URL to the document serving as a source file for the document embedded into the web page.
     * */
    embedUrl?: string;
  
    /**
     * Defines the absolute URL to the document which will open in full screen mode.
     * */
    fullscreenUrl?: string;
  
    /**
     * Defines the absolute URL that will allow the document to be saved onto the user personal computer.
     * */
    saveUrl?: string;
  
    /**
     * Defines the absolute URL that will allow other users to share this document.
     * */
    shareUrl?: string;
  
    /**
     * Defines the place for the embedded viewer toolbar, can be either top or bottom.
     * */
    toolbarDocked?: "top" | "bottom";
  }
  
  /**
   * Defines the presence or absence of the documents in the Open Recent... menu option.
   * */
  interface Recent {
    /**
     * The folder where the document is stored (can be empty in case the document is stored in the root folder)
     * type: string
     * example: "Example Files"
     * */
    folder?: string;
  
    /**
     * The document title that will be displayed in the Open Recent... menu option
     * type: string
     * example: "exampledocument1.docx"
     * */
    title?: string;
  
    /**
     * The absolute URL to the document where it is stored
     * type: string
     * example: "https://example.com/exampledocument1.docx".
     * */
    url?: string;
  }
  
  /**
   * Defines the user currently viewing or editing the document.
   * */
  interface User {
    /**
     * The unique identification of the user,
     * type: string,
     * example: "78e1e841";
     * */
    id?: string;
  
    /**
     * The full name of the user.
     * This property is used since version 4.2,
     * type: string,
     * example: "John Smith".
     * */
    name?: string;
  
    group?:string;
  }
  
  interface Plugins {
    autostart?: string[];
    pluginsData?: string[];
  }
  
  /**
   * contains additional parameters for the document (document author, folder where the document is stored, creation date, sharing settings)
   * */
  interface Info {
    /**
     * Defines the name of the document author/creator.
     * */
     owner?: string;
  
    /**
     * Defines the document creation date.
     * */
    created?: string;
  
    /**
     * Defines the folder where the document is stored (can be empty in case the document is stored in the root folder).
     * */
     folder?: string;
  
    /**
     * Displays the information about the settings which allow to share the document with other users:
     * */
    sharingSettings?: SharingSetting[];
  
    uploaded?:string
  
    favorite?:boolean
  }
  
  /**
   * The document permission section allows to change the permission for the document to be edited and downloaded or not.
   * */
  interface Permissions {
  
    copy?:boolean;
    /**
     * Defines if the document can be commented or not.
     * In case the commenting permission is set to "true" the document side bar will contain the Comment menu option;
     * the document commenting will only be available for the document editor if the mode parameter is set to edit.
     * The default value coincides with the value of the edit parameter.
     * */
    comment?: boolean;
  
    /**
     * Defines if the document can be downloaded or only viewed or edited online.
     * In case the downloading permission is set to "false" the Download as... menu option will be absent from the File menu.
     * The default value is true.
     * */
    download?: boolean;
  
    /**
     * Defines if the document can be edited or only viewed.
     * In case the editing permission is set to "true" the File menu will contain the Edit Document menu option;
     * please note that if the editing permission is set to "false" the document will be opened in viewer
     * and you will not be able to switch it to the editor even if the mode parameter is set to edit.
     * The default value is true.
     * */
    edit?: boolean;
  
    /**
     * Defines if the forms can be filled.
     * Filling in forms will only be available for the document editor if the mode parameter is set to edit.
     * The default value coincides with the value of the edit or the review parameter.
     * */
    fillForms?: boolean;
  
    /**
     * Defines if the document can be printed or not.
     * In case the printing permission is set to "false" the Print menu option will be absent from the File menu.
     * The default value is true.
     * */
    print?: boolean;
  
    /**
     * Defines if the document can be reviewed or not.
     * In case the reviewing permission is set to "true" the document status bar will contain the Review menu option;
     * the document review will only be available for the document editor if the mode parameter is set to edit.
     * The default value coincides with the value of the edit parameter.
     * */
    review?: boolean;
  
    /**
     * Defines whether user can view in readable mode
     * */
    reader?: boolean;
  
    /**
     * Defines whether user can rename
     * default = false
     * */
    rename?: boolean;
  
    /**
     * Defines whether user can change history
     * default = false
     * */
    changeHistory?: boolean;
  
    /**
     * Defines whether user can add, remove and save filter in the spreadsheet
     * default = true
     * */
    modifyFilter?: boolean;
  
    /**
     * Defines whether user can modify content controls in documenteditor
     * default = true
     * */
    modifyContentControl?: boolean;
  
    editCommentAuthorOnly?: boolean;
  
    deleteCommentAuthorOnly?: boolean;
  
    reviewGroups:string[];
  
    commentGroups:groupcomments;
  
    userInfoGroups:string[];
  
    protect:boolean;
  
  }
  
  interface groupcomments {
    view:string[];
    edit:string[];
    remove:string[];
  }
  /**
   * Contains the information which will be displayed int the editor About section and visible to all the editor users.
   * */
  interface Customer {
    /**
     * postal address of the company or person who gives access to the editors or the editor authors,
     * type: string,
     * example: "My City, 123a-45";
     * */
    address: string;
  
    /**
     * some additional information about the company or person you want the others to know,
     * type: string,
     * example: "Some additional information";
     * */
    info: string;
  
    /**
     * the path to the image logo (there are no special recommendations for this file, but it would be better if it were in .png format with transparent background).
     * The image must have the following size: 432x70,
     * type: string,
     * example: "https://example.com/logo-big.png".
     * */
    logo: string;
  
    /**
     * email address of the company or person who gives access to the editors or the editor authors,
     * type: string,
     * example: "john@example.com".
     * */
    mail: string;
  
    /**
     * the name of the company or person who gives access to the editors or the editor authors,
     * type: string,
     * example: "John Smith and Co.";
     * */
    name: string;
  
    /**
     * home website address of the above company or person,
     * type: string,
     * example: "example.com".
     * */
    www: string;
    phone: string;
    logoDark:string;
  }
  
  /**
   * Defines settings for the Feedback & Support menu button. Can be either boolean (simply displays or hides the Feedback & Support menu button) or object.
   * */
  interface Feedback {
    /**
     * the absolute URL to the website address which will be opened when clicking the Feedback & Support menu button,
     * type: string,
     * example: "https://example.com";
     * */
    url?: string;
  
    /**
     * show or hide the Feedback & Support menu button,
     * type: boolean,
     * example: true.
     * */
    visible?: boolean;
  }
  
  /**
   * Defines settings for the Go to Documents menu button and upper right corner button.
   * */
  interface Goback {
    /**
     * open the website in the new browser tab/window (if the value is set to true)
     * or the current tab (if the value is set to false) when the Go to Documents button is clicked.
     * The default value is true,
     * 
     * */
    blank?: boolean;
  
    /**
     * the text which will be displayed for the Go to Documents menu button and upper right corner button (i.e. instead of Go to Documents),
     * */
    text?: string;
  
    /**
     * the absolute URL to the website address which will be opened when clicking the Go to Documents menu button,
     * */
    url?: string;
    requestClose?:boolean;
  }
  
  /**
   * Changes the image file at the top left corner of the Editor header.
   * The recommended image height is 20 pixels.
   * */
  interface Logo {
    /**
     * path to the image file used to show in common work mode (i.e. in view and edit modes for all editors).
     * The image must have the following size: 172x40,
     * type: string,
     * example: "https://example.com/logo.png";
     * */
    image?: string;
  
    /**
     * path to the image file used to show in the embedded mode (see the config section to find out how to define the embedded document type).
     * The image must have the following size: 248x40,
     * type: string,
     * example: "https://example.com/logo_em.png";
     * */
    imageEmbedded?: string;
  
    /**
     * the absolute URL which will be used when someone clicks the logo image (can be used to go to your web site, etc.).
     * Leave as an empty string or null to make the logo not clickable,
     * type: string,
     * example: "https://example.com".
     * */
    url?: string | null;
  
    imageDark?:string;
  }
  
  /**
   * Displays the information about the settings which allow to share the document with other users:
   * 
   * */
  interface SharingSetting {
    /**
     * the access rights for the user with the name below. Can be "Full Access", "Read Only" or "Deny Access"
     * type: string
     * example: "Full Access"
     * */
    permissions: "Full Access" | "Read Only" | "Deny Access";
  
    /**
     * The Display Name of the user the document will be shared with
     * type: string
     * example: "John Smith".
     * */
    user: string;
  
    isLink:boolean
  }
  
  /**
   * 
   * */
  export interface ChangeHistory {
    currentVersion?: number;
    history?: History[];
  }
  
  /**
   * 
   * */
  interface History {
    changes?: any; // the changes from the history object returned after saving the document
    created?: string;
    key?: string;
    serverVersion?: string; // the serverVersion from the history object returned after saving the document
    user?: User;
    version?: number;
  }
  
  /**
   * 
   * */
  export interface HistoryData {
    changesUrl?: string; // the changesUrl from the JSON object returned after saving the document
    key?: string;
    previous?: Previous;
    url?: string;
    version: number;
  }
  
  /**
   * 
   * */
  export interface Previous {
    key?: string;
    url?: string;
  }
  
  export enum EditorType {
    FreeEdit = 'FE', //This is used for all types other than Review
    ReviewHistory = 'RH',
    ReviewOwner = 'RO',
    ReviewByReviewer = 'RR'
  }
  export interface UserEditorConfig {
    OrgID?: string;
    FileID?: string;
    OwnerID?: string;
    UserID?: string;
    IsEditMode?: boolean;
    CanEdit?: boolean;
    CanReview?: boolean;
    ReviewID?: string;
    IsOwner?: boolean;
}

export interface UserEditConfig {
    UserConfigID?: string;
    TempFileID?: string;
    FileStore?: 'TEMP' | 'FILE';
    FileName?: string;
    FileType?: string;
    UniKey?: string;
    OwnerID?: string;
    OwnerName?: string;
    UserID?: string;
    UserName?: string;
    IsEditMode?: boolean;
    CanEdit?: boolean;
    CanReview?: boolean;
    IsOwner?: boolean;
}


  
export enum FileStoreType {
    TEMP = 'TEMP',
    DOC = 'DOC',
    MERGE = 'MERGE',
    FILE = 'FILE' // File Store
}

export interface PB_TEMP_FILE_STORE {
    PK_TEMP_FILE_STORE_ID?: string;
    IS_ACTIVE?: boolean;
    CREATED_DATE_ONLY?: Date;
    CREATED_DATE?: Date;
    FILE_NAME?: string;
    FILE_EXT?: string;
    FILE_BYTES?: any;
}

export interface ContributorTaskDocInfo {
    TaskContributorID?: string;
    DocumentTypeID?: string;
    DocumentTypeName?:string;
    ProjectID?: string;
    MilestoneID?: string;
    MI_TaskID?: string;
    TaskTypeID?: string;
    TaskTypeCode?: string;
    OwnerID?: string;
    CreatorID?: string;
    IsDocumentUploaded?: boolean;
    UserEditConfig?: UserEditConfig;
}
